import { Currency } from "@redotech/money/currencies";
import {
  centsToDollars,
  dollarsToCents,
  roundMoney,
  UsdCents,
  UsdDollars,
} from "@redotech/money/usd-amounts";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  isReturnShippingCatalogProduct,
  returnShippingCatalogEntry,
  ReturnShippingCatalogProduct,
  softwareCatalogEntry,
} from "./billing-product-catalog";
import {
  isSupportedBillingEngineInvoiceType,
  LineItem,
  ServiceInvoiceType,
  SupportedBillingEngineInvoiceType,
  supportedBillingEngineInvoiceTypes,
} from "./service-invoice";

export const BUNDLE_DISCOUNT_TYPE_HELPER = "bundle-discount";

export function isBundleDiscountLineItem(
  lineItem: { typeHelper?: string } | null | undefined,
): boolean {
  return lineItem?.typeHelper === BUNDLE_DISCOUNT_TYPE_HELPER;
}

/** `entire_provider`: percent-off applies to every priced line the provider emits. */
export type EngineBundleDiscountMode = "entire_provider";

export function engineProviderBundleDiscountMode(
  productType: SupportedBillingEngineInvoiceType,
): EngineBundleDiscountMode {
  switch (productType) {
    case ServiceInvoiceType.MARKETING_EMAIL:
    case ServiceInvoiceType.MARKETING_SMS:
    case ServiceInvoiceType.ORDER_TRACKING:
    case ServiceInvoiceType.RETURN_AI_EXCHANGE:
    case ServiceInvoiceType.RETURNS:
    case ServiceInvoiceType.CHECKOUT_OPTIMIZATION:
    case ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION:
    case ServiceInvoiceType.AEO:
    case ServiceInvoiceType.IMS:
    case ServiceInvoiceType.IMS_FORECASTING:
    case ServiceInvoiceType.RECOVER:
    case ServiceInvoiceType.DISPUTES:
    case ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS:
    case ServiceInvoiceType.LCG_COMMIT:
    case ServiceInvoiceType.CTX_USAGE:
    case ServiceInvoiceType.SUPPORT_BASE:
    case ServiceInvoiceType.SUPPORT_AI_USAGE:
    case ServiceInvoiceType.DIMENSIONAL_PACKAGING:
    case ServiceInvoiceType.WMS:
    case ServiceInvoiceType.OMS:
      return "entire_provider";
    default:
      return assertNever(productType);
  }
}

export function formatCatalogUsd(amount: UsdDollars): string {
  const rounded = roundMoney({ amount, decimals: 2 });
  if (Number.isInteger(rounded)) {
    return `$${rounded}`;
  }
  return `$${rounded.toFixed(2)}`;
}

export function formatBundleCatalogSummary(params: {
  subscriptionPrice: UsdDollars | null;
  periodLabel: string | null;
  usageTerms?: readonly string[];
}): string {
  const usageTerms = (params.usageTerms ?? []).filter(
    (term) => term.length > 0,
  );
  const parts: string[] = [];
  const hasUsage = usageTerms.length > 0;
  if (
    params.subscriptionPrice != null &&
    params.periodLabel != null &&
    (params.subscriptionPrice > 0 || !hasUsage)
  ) {
    parts.push(
      `${formatCatalogUsd(params.subscriptionPrice)} / ${params.periodLabel}`,
    );
  }
  parts.push(...usageTerms);
  if (parts.length === 0) {
    return "List terms are configured on this product.";
  }
  return parts.join(" · ");
}

export function engineBillingProductLabel(
  productType: SupportedBillingEngineInvoiceType,
): string {
  return softwareCatalogEntry(productType).label;
}

const billingBundleSoftwareMemberSchema_ = z.object({
  billType: z.literal("software"),
  productType: createEnum(supportedBillingEngineInvoiceTypes, {
    enumName: "SupportedBillingEngineInvoiceType",
  }),
  /** 0 = anchor (must stay billing-active). 100 = entire provider complimentary. */
  percentOff: z.number().int().min(0).max(100),
});
const billingBundleReturnShippingMemberSchema_ = z.object({
  billType: z.literal("return_shipping"),
  product: createEnum(ReturnShippingCatalogProduct, {
    enumName: "ReturnShippingCatalogProduct",
  }),
  percentOff: z.literal(0),
});
export const billingBundleMemberSchema = z.discriminatedUnion("billType", [
  billingBundleSoftwareMemberSchema_,
  billingBundleReturnShippingMemberSchema_,
]);
export type BillingBundleMember = z.infer<typeof billingBundleMemberSchema>;

export const billingBundleSchema = z.object({
  id: z.string().min(1),
  members: z.array(billingBundleMemberSchema).min(1),
});
export type BillingBundle = z.infer<typeof billingBundleSchema>;

export function isSoftwareBundleMember(
  member: BillingBundleMember,
): member is Extract<BillingBundleMember, { billType: "software" }> {
  return member.billType === "software";
}

export function bundleMemberKey(member: BillingBundleMember): string {
  switch (member.billType) {
    case "software":
      return softwareCatalogEntry(member.productType).id;
    case "return_shipping":
      return returnShippingCatalogEntry(member.product).id;
    default:
      return assertNever(member);
  }
}

function bundleProductSignature(bundle: BillingBundle): string {
  return bundle.members
    .map((member) => bundleMemberKey(member))
    .sort()
    .join("\n");
}

export function reconcileBillingBundleIds(
  local: readonly BillingBundle[],
  server: readonly BillingBundle[],
): BillingBundle[] {
  const usedServerIds = new Set<string>();
  return local.map((bundle) => {
    if (/^[0-9]+$/.test(bundle.id)) {
      const byId = server.find((item) => item.id === bundle.id);
      if (byId) {
        usedServerIds.add(byId.id);
        return { ...bundle, id: byId.id };
      }
    }
    const signature = bundleProductSignature(bundle);
    const match = server.find(
      (item) =>
        !usedServerIds.has(item.id) &&
        bundleProductSignature(item) === signature,
    );
    if (!match) {
      return bundle;
    }
    usedServerIds.add(match.id);
    return { ...bundle, id: match.id };
  });
}

export function bundleMemberLabel(member: BillingBundleMember): string {
  switch (member.billType) {
    case "software":
      return softwareCatalogEntry(member.productType).label;
    case "return_shipping":
      return returnShippingCatalogEntry(member.product).label;
    default:
      return assertNever(member);
  }
}

export function storedBundleMemberProduct(member: BillingBundleMember): string {
  switch (member.billType) {
    case "software":
      return member.productType;
    case "return_shipping":
      return returnShippingCatalogEntry(member.product).id;
    default:
      return assertNever(member);
  }
}

export function parseStoredBundleMember(
  stored: string,
  percentOff: number,
): BillingBundleMember | null {
  const parsed = parseStoredBundleMemberUnchecked(stored, percentOff);
  const result = billingBundleMemberSchema.safeParse(parsed);
  return result.success ? result.data : null;
}

function isStoredSoftwareInvoiceType(
  stored: string,
): stored is SupportedBillingEngineInvoiceType {
  for (const productType of supportedBillingEngineInvoiceTypes) {
    if (productType === stored) {
      return true;
    }
  }
  return false;
}

function parseStoredBundleMemberUnchecked(
  stored: string,
  percentOff: number,
): unknown {
  if (stored.startsWith("return_shipping:")) {
    return {
      billType: "return_shipping",
      product: stored.slice("return_shipping:".length),
      percentOff,
    };
  }
  if (stored.startsWith("software:")) {
    return {
      billType: "software",
      productType: stored.slice("software:".length),
      percentOff,
    };
  }
  if (isStoredSoftwareInvoiceType(stored)) {
    return { billType: "software", productType: stored, percentOff };
  }
  if (isReturnShippingCatalogProduct(stored)) {
    return { billType: "return_shipping", product: stored, percentOff };
  }
  return null;
}

const billingBundleCatalogSoftwareProductSchema_ = z.object({
  billType: z.literal("software"),
  productType: createEnum(supportedBillingEngineInvoiceTypes, {
    enumName: "SupportedBillingEngineInvoiceType",
  }),
  enabled: z.boolean(),
  summary: z.string(),
});
const billingBundleCatalogReturnShippingProductSchema_ = z.object({
  billType: z.literal("return_shipping"),
  product: createEnum(ReturnShippingCatalogProduct, {
    enumName: "ReturnShippingCatalogProduct",
  }),
  enabled: z.boolean(),
  summary: z.string(),
});
export const billingBundleCatalogProductSchema = z.discriminatedUnion(
  "billType",
  [
    billingBundleCatalogSoftwareProductSchema_,
    billingBundleCatalogReturnShippingProductSchema_,
  ],
);
export type BillingBundleCatalogProduct = z.infer<
  typeof billingBundleCatalogProductSchema
>;

export function isSoftwareCatalogProduct(
  product: BillingBundleCatalogProduct,
): product is Extract<BillingBundleCatalogProduct, { billType: "software" }> {
  return product.billType === "software";
}

export function catalogProductId(product: BillingBundleCatalogProduct): string {
  if (product.billType === "software") {
    return softwareCatalogEntry(product.productType).id;
  }
  return returnShippingCatalogEntry(product.product).id;
}

function isBundleMemberActive(
  member: BillingBundleMember,
  softwareActive: ReadonlySet<ServiceInvoiceType>,
  returnShippingActive: ReadonlySet<ReturnShippingCatalogProduct>,
): boolean {
  switch (member.billType) {
    case "software":
      return softwareActive.has(member.productType);
    case "return_shipping":
      return returnShippingActive.has(member.product);
    default:
      return assertNever(member);
  }
}

export interface ResolvedBundleDiscount {
  bundle: BillingBundle;
  percentOff: number;
}

export function billingBundlesValidationError(
  bundles: readonly BillingBundle[],
): string | null {
  const seen = new Set<string>();
  for (const bundle of bundles) {
    let hasAnchor = false;
    for (const member of bundle.members) {
      if (isSoftwareBundleMember(member)) {
        engineProviderBundleDiscountMode(member.productType);
      } else if (member.percentOff !== 0) {
        return "Return-shipping products cannot be discounted in a billing bundle";
      }
      if (member.percentOff <= 0) {
        hasAnchor = true;
      }
      const key = bundleMemberKey(member);
      if (seen.has(key)) {
        return "A product can only belong to one billing bundle";
      }
      seen.add(key);
    }
    if (!hasAnchor) {
      return "Each billing bundle must include at least one undiscounted product";
    }
  }
  return null;
}

/** Discount applies iff this product is discounted and every 0% anchor is still billing-active. */
export function resolveEntireProviderBundleDiscount(params: {
  productType: ServiceInvoiceType;
  bundles: readonly BillingBundle[];
  billingActiveProductTypes: ReadonlySet<ServiceInvoiceType>;
  returnShippingActive?: ReadonlySet<ReturnShippingCatalogProduct>;
}): ResolvedBundleDiscount | null {
  if (!isSupportedBillingEngineInvoiceType(params.productType)) {
    return null;
  }
  engineProviderBundleDiscountMode(params.productType);
  const returnShippingActive = params.returnShippingActive ?? new Set();

  for (const bundle of params.bundles) {
    const member = bundle.members.find(
      (item) =>
        item.billType === "software" && item.productType === params.productType,
    );
    if (!member || member.percentOff <= 0) {
      continue;
    }

    const anchors = bundle.members.filter((item) => item.percentOff <= 0);
    if (anchors.length === 0) {
      continue;
    }
    const anchorsActive = anchors.every((anchor) =>
      isBundleMemberActive(
        anchor,
        params.billingActiveProductTypes,
        returnShippingActive,
      ),
    );
    if (!anchorsActive) {
      continue;
    }

    return { bundle, percentOff: member.percentOff };
  }

  return null;
}

export function getProductBundleMember(
  bundles: readonly BillingBundle[],
  productType: ServiceInvoiceType,
): { bundle: BillingBundle; member: BillingBundleMember } | null {
  for (const bundle of bundles) {
    const member = bundle.members.find(
      (item) =>
        item.billType === "software" && item.productType === productType,
    );
    if (member) {
      return { bundle, member };
    }
  }
  return null;
}

export function listTotalOfPricedLineItems(lineItems: readonly LineItem[]): {
  total: UsdDollars;
  currency: Currency;
} {
  let total = 0;
  let currency: Currency = Currency.USD;
  for (const lineItem of lineItems) {
    if (lineItem.typeHelper === BUNDLE_DISCOUNT_TYPE_HELPER) {
      continue;
    }
    if (lineItem.price == null) {
      continue;
    }
    total += lineItem.price;
    currency = lineItem.currency;
  }
  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    total: roundMoney({ amount: total as UsdDollars, decimals: 2 }),
    currency,
  };
}

export function bundleDiscountLineItem(params: {
  percentOff: number;
  listTotal: UsdDollars;
  currency: Currency;
  productType: SupportedBillingEngineInvoiceType;
}): LineItem {
  const listCents = dollarsToCents(params.listTotal);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const discountCents = Math.round(
    (listCents * params.percentOff) / 100,
  ) as UsdCents;
  const discountDollars = centsToDollars(discountCents);
  return {
    name: `Bundle discount (${params.percentOff}% off ${engineBillingProductLabel(params.productType)})`,
    nameDescription:
      "List prices above are unchanged. This line is the bundle discount.",
    price: -discountDollars,
    currency: params.currency,
    quantity: 1,
    typeHelper: BUNDLE_DISCOUNT_TYPE_HELPER,
  };
}

/** Keeps list-priced provider lines and appends one negative discount for the whole provider. */
export function applyEntireProviderBundleDiscount(params: {
  lineItems: LineItem[];
  percentOff: number;
  productType: SupportedBillingEngineInvoiceType;
}): LineItem[] {
  if (params.percentOff <= 0) {
    return params.lineItems;
  }
  engineProviderBundleDiscountMode(params.productType);

  const withoutExistingDiscount = params.lineItems.filter(
    (lineItem) => lineItem.typeHelper !== BUNDLE_DISCOUNT_TYPE_HELPER,
  );
  const { total, currency } = listTotalOfPricedLineItems(
    withoutExistingDiscount,
  );
  if (
    !bundlePercentOffApplies({
      listTotal: total,
      percentOff: params.percentOff,
    })
  ) {
    return withoutExistingDiscount;
  }

  return [
    ...withoutExistingDiscount,
    bundleDiscountLineItem({
      percentOff: params.percentOff,
      listTotal: total,
      currency,
      productType: params.productType,
    }),
  ];
}

export function applyResolvedBundleDiscount(params: {
  lineItems: LineItem[];
  productType: ServiceInvoiceType;
  discount: ResolvedBundleDiscount | null;
}): LineItem[] {
  if (!params.discount) {
    return params.lineItems;
  }
  if (!isSupportedBillingEngineInvoiceType(params.productType)) {
    return params.lineItems;
  }
  const mode = engineProviderBundleDiscountMode(params.productType);
  switch (mode) {
    case "entire_provider":
      return applyEntireProviderBundleDiscount({
        lineItems: params.lineItems,
        percentOff: params.discount.percentOff,
        productType: params.productType,
      });
    default:
      return assertNever(mode);
  }
}

export function parseBillingBundles(raw: unknown): BillingBundle[] {
  const parsed = z.array(billingBundleSchema).safeParse(raw ?? []);
  return parsed.success ? parsed.data : [];
}

export function bundleDiscountMerchantCopy(params: {
  bundles: readonly BillingBundle[];
  productType: ServiceInvoiceType;
  appliedPercentOff?: number | null;
}): string | null {
  if (params.appliedPercentOff === null) {
    return null;
  }
  if (!isSupportedBillingEngineInvoiceType(params.productType)) {
    return null;
  }
  const found = getProductBundleMember(params.bundles, params.productType);
  if (!found || found.member.percentOff <= 0) {
    return null;
  }

  const labels: string[] = [];
  for (const member of found.bundle.members) {
    if (member.percentOff > 0) {
      continue;
    }
    labels.push(bundleMemberLabel(member));
  }

  let anchorList = "the bundled product";
  if (labels.length === 1 && labels[0]) {
    anchorList = labels[0];
  } else if (labels.length > 1) {
    const last = labels[labels.length - 1];
    if (last) {
      anchorList = `${labels.slice(0, -1).join(", ")} and ${last}`;
    }
  }

  const productLabel = engineBillingProductLabel(params.productType);
  const percentOff = params.appliedPercentOff ?? found.member.percentOff;
  const endsCopy =
    labels.length > 1
      ? "If any of those stop billing, this product bills at list next cycle."
      : `If ${anchorList} billing ends, this product bills at list next cycle.`;
  return `${productLabel} is ${percentOff}% off the entire provider while billed with ${anchorList}. ${endsCopy}`;
}

/** Percent-off only applies to a positive list total — credits stay whole. */
export function bundlePercentOffApplies(params: {
  listTotal: UsdDollars;
  percentOff: number;
}): boolean {
  return params.percentOff > 0 && params.listTotal > 0;
}

export function discountedInvoiceTotalDollars(params: {
  listTotal: UsdDollars;
  percentOff: number;
}): UsdDollars {
  if (!bundlePercentOffApplies(params)) {
    return params.listTotal;
  }
  const listCents = dollarsToCents(
    roundMoney({ amount: params.listTotal, decimals: 2 }),
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const discountCents = Math.round(
    (listCents * params.percentOff) / 100,
  ) as UsdCents;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return centsToDollars((listCents - discountCents) as UsdCents);
}
